import { Constants } from '@pwc/pwc-deals-web-common';
import cloneDeep from 'lodash/cloneDeep';
import _ from 'lodash';
import get from 'lodash/get';
import attempt from 'lodash/attempt';
import { TABLEAU_LIBRARY_ITEM_TYPES } from 'common/components/SectionArea/libraryItemUtils.ts';
import { typeToName, formatTypeMap } from './constants';
import {
  getExcelLibraryItemsByGroup,
  getPptLibraryItemsByGroup,
  getRichTextLibraryItemsByGroup,
} from '../../../../contentLibrary/libraryItemService';
import { topicPanelType } from '../Topic/mapTopic';

export const getExcelPPTItem = (topic, panel) => {
  const { libraryItemId, autoSync } = panel;
  const { libraryItemGroups } = topic;
  if (libraryItemGroups) {
    const { excelPptLibraryItemList = [] } = libraryItemGroups;
    for (let index = 0; index < excelPptLibraryItemList.length; index += 1) {
      const item = excelPptLibraryItemList[index];
      const excelItem = item.excelPptLibraryItems?.find(
        (i) => i.id === libraryItemId
      );
      if (excelItem) {
        const { type, data } = excelItem;
        const { chartName, name, groupName, slideNumber, formatType } = data;
        const worksheetName =
          type === Constants.LIBRARY_ITEM_TYPES.EXCEL_CHART.value
            ? `${data.worksheetName} ${chartName}`
            : name || groupName;
        const { sharePointItemUrl } = item;
        const fileName = sharePointItemUrl
          ? sharePointItemUrl.split('/').pop()
          : '';

        return {
          worksheetName,
          sharePointItemUrl,
          fileName,
          type,
          groupName,
          autoSync,
          libraryItemId,
          formatType,
          slideNumber,
        };
      }
    }
  }
  return null;
};

export const getDynamicExcelObject = (
  topic,
  panel,
  sharedItems,
  panelIndex
) => {
  const { libraryItemId, autoSync } = panel;
  const { libraryItemGroups } = topic;

  if (
    panel.sharedContent &&
    panel.type === topicPanelType.dynamicExcelCellRange &&
    sharedItems
  ) {
    try {
      const pascalCaseItem = JSON.parse(sharedItems[panelIndex].data);
      const sharedItem = _.mapKeys(pascalCaseItem, (v, k) => _.camelCase(k));
      return {
        ...sharedItems[panelIndex],
        data: sharedItem,
        status: topic.content.versionStatus,
        sharedStatus: sharedItems[panelIndex].status,
      };
    } catch (error) {
      // eslint-disable-next-line
      console.error(
        'Unable to parse shared item, sharedItems',
        sharedItems,
        'sharedItems[panelIndex]',
        sharedItems && panelIndex !== undefined
          ? sharedItems[panelIndex]
          : undefined
      );
      return null;
    }
  }

  if (libraryItemGroups) {
    const { dynamicExcelLibraryItemList = [] } = libraryItemGroups;
    for (
      let index = 0;
      index < dynamicExcelLibraryItemList.length;
      index += 1
    ) {
      const item = dynamicExcelLibraryItemList[index];
      const dynamicExcelItem = item.dynamicExcelLibraryItems?.find(
        (i) => i.id === libraryItemId
      );
      if (dynamicExcelItem) {
        return {
          sharePointItemUrl: item.sharePointItemUrl,
          ...dynamicExcelItem,
          status: topic.content.versionStatus,
          autoSync,
        };
      }
    }
  }
  return null;
};

const transformExcelPptGroupResponse = (response) => {
  const { fileContents } = response;
  return fileContents.map((item) => {
    return {
      id: item.id,
      name: item.name,
      type: item.type,
      content: item.value,
      autoSync: item.autoSync,
      formatType: item.formatType || null,
      apiErrorCode: item.error && item.error.apiErrorCode,
      errorCode: item.error && item.error.errorCode,
      // ppt (500) returns different error response
      error:
        item.type === 500
          ? item.error && item.error.isError
          : item.error || null,
    };
  });
};

export const fetchPptItems = async (projectId, excelPptLibraryItemList) => {
  const promiseArr = [];

  excelPptLibraryItemList.forEach((group) => {
    const reqBody = {
      groupedByKey: group.groupedByKey,
      sharePointItemUrl: group.sharePointItemUrl,
      driveId: group.driveId,
      driveItemId: group.driveItemId,
      siteId: group.siteId,
    };
    reqBody.pptLibraryItems = group.excelPptLibraryItems
      .slice()
      .filter(
        (i) => i.type === Constants.LIBRARY_ITEM_TYPES.POWER_POINT_GROUP.value
      )
      .map((libItem) => {
        const cloned = cloneDeep(libItem);
        const { data } = cloned;
        data.formatType = formatTypeMap[libItem.type];
        data[typeToName[libItem.type]] = data.name || data.groupName;
        return { ...cloned, data };
      });
    if (reqBody.pptLibraryItems.length) {
      promiseArr.push(
        getPptLibraryItemsByGroup(projectId, reqBody).catch((err) => ({
          fileName: reqBody.sharePointItemUrl,
          groupedByKey: reqBody.groupedByKey,
          fileContents: reqBody.pptLibraryItems.map((item) => ({
            ...item,
            type: undefined,
            formatType: item?.data?.formatType,
            error: { isError: true, message: err },
            autoSync: item,
          })),
        }))
      );
    }
  });
  const data = await Promise.all(promiseArr);
  const transformedData = data.map(transformExcelPptGroupResponse).flat();
  return transformedData;
};

export const fetchExcelItems = async (projectId, excelPptLibraryItemList) => {
  const promiseArr = [];

  const requestedLibItems = [];

  excelPptLibraryItemList.forEach((group) => {
    const reqBody = {
      groupedByKey: group.groupedByKey,
      sharePointItemUrl: group.sharePointItemUrl,
      driveId: group.driveId,
      driveItemId: group.driveItemId,
      siteId: group.siteId,
    };
    const requestDataForLibItemsInBothTypes = [];
    requestedLibItems.push(group.excelPptLibraryItems);
    group.excelPptLibraryItems
      .slice()
      .filter(
        (i) => i.type !== Constants.LIBRARY_ITEM_TYPES.POWER_POINT_GROUP.value
      )
      .forEach((libItem) => {
        const cloned = cloneDeep(libItem);
        const { data } = cloned;

        data[typeToName[libItem.type]] = data.name || data.groupName;
        requestDataForLibItemsInBothTypes.push({
          ...cloned,
          data: { ...data, formatType: formatTypeMap[libItem.type] },
        });
        requestDataForLibItemsInBothTypes.push({
          ...cloned,
          data: { ...data, formatType: 'SVG' },
        });
      });
    reqBody.excelLibraryItems = requestDataForLibItemsInBothTypes;

    if (reqBody.excelLibraryItems.length) {
      promiseArr.push(
        getExcelLibraryItemsByGroup(projectId, reqBody).catch((err) => ({
          fileName: reqBody.sharePointItemUrl,
          groupedByKey: reqBody.groupedByKey,
          fileContents: reqBody.excelLibraryItems.map((item) => ({
            ...item,
            type: undefined,
            formatType: item?.data?.formatType,
            error: { isError: true, message: err },
            autoSync: item,
          })),
        }))
      );
    }
  });
  const data = await Promise.all(promiseArr);
  const transformedData = data.map(transformExcelPptGroupResponse).flat();

  const originalRequestedLibItemsArray = requestedLibItems.flat();
  const dataWithInfoAboutOriginalRequestedType = transformedData.map(
    (result) => {
      const requestedLib = originalRequestedLibItemsArray.find(
        (original) => original.id === result.id
      );

      return {
        ...result,
        isDisplayedFormatType:
          result.formatType === requestedLib.data.formatType,
      };
    }
  );

  return dataWithInfoAboutOriginalRequestedType;
};

const transformRichTextData = (item) => {
  const {
    richTextContent: { richTextData },
  } = item;
  return {
    id: item.id,
    type: item.type,
    content: richTextData,
    error: item.error || null,
  };
};

export const fetchRichTextItems = async (
  projectId,
  richTextLibraryItemList,
  contextStatusInfoItem,
  isPwcUser
) => {
  const richTextLibItems = richTextLibraryItemList
    .reduce((accum, item) => {
      const { libraryItemId: itemId } = item;
      const parsedId = Number.isInteger(parseInt(itemId, 10))
        ? parseInt(itemId, 10)
        : null;
      return accum.concat([parsedId]);
    }, [])
    .filter((item) => item !== null);
  const reqBody = {
    libraryItemIds: richTextLibItems,
  };
  const response = await getRichTextLibraryItemsByGroup(
    projectId,
    reqBody,
    contextStatusInfoItem,
    isPwcUser
  );
  const { richTextLibraryItems } = response;
  return richTextLibraryItems.map(transformRichTextData);
};

export const tryJsonParse = (str) => attempt(JSON.parse.bind(null, str));

export const getIsPowerBiReport = (panel) =>
  panel.type === Constants.LIBRARY_ITEM_TYPES.POWER_BI_REPORT.value ||
  panel.type === Constants.LIBRARY_ITEM_TYPES.POWER_BI_REPORT_PAGE.value ||
  panel.type === 8 ||
  panel.type === 6;

export const getIsTableauReport = (panel) =>
  panel.type === TABLEAU_LIBRARY_ITEM_TYPES.TableauReportPage ||
  panel.type === TABLEAU_LIBRARY_ITEM_TYPES.TableauReport ||
  panel.type === 9 ||
  panel.type === 10;

export const mapOnlyPowerBiTopic = (topic) => {
  const clonedTopic = cloneDeep(topic);

  clonedTopic.content.panels = clonedTopic.content.panels.map((panel) => {
    if (getIsPowerBiReport(panel)) {
      if (
        topic.libraryItemGroups &&
        topic.libraryItemGroups.powerBiLibraryItemList
      ) {
        const foundPowerBi =
          topic.libraryItemGroups.powerBiLibraryItemList.find((powerBiItem) =>
            powerBiItem.powerBiLibraryItems.filter(
              (powerBiLibraryItemschild) =>
                powerBiLibraryItemschild.id === panel.libraryItemId
            )
          );

        if (foundPowerBi) {
          return {
            ...panel,
            reportId: foundPowerBi.reportId,
            contentType: 'power-bi',
          };
        }
      } else if (topic.items) {
        // Items data
        const foundPowerBi = topic.items.find(
          (item) => item.id === panel.libraryItemId
        );

        if (foundPowerBi) {
          const parsedData = tryJsonParse(foundPowerBi.data);
          return {
            ...panel,
            reportId: parsedData.ReportId,
            contentType: 'power-bi',
          };
        }
      } else {
        // eslint-disable-next-line no-console
        console.error('Not found powerbi items:', topic);
      }
    }
    if (getIsTableauReport(panel)) {
      if (
        topic.libraryItemGroups &&
        topic.libraryItemGroups.tableauLibraryItemList
      ) {
        const foundItems = _.filter(
          topic.libraryItemGroups.tableauLibraryItemList,
          {
            tableauLibraryItems: [{ id: panel.libraryItemId }],
          }
        );
        if (foundItems.length) {
          return {
            ...panel,
            data: {
              ...foundItems[0].tableauLibraryItems[0],
              tabName: foundItems[0].tableauLibraryItems[0].data.tabName,
            },
            reportId: foundItems[0].reportId,
            contentType: 'tableau',
          };
        }
      } else if (topic.items) {
        // Items data
        const foundItem = topic.items.find(
          (item) => item.id === panel.libraryItemId
        );

        if (foundItem) {
          const parsedData = tryJsonParse(foundItem.data);
          return {
            ...panel,
            reportId: parsedData.ReportId,
            contentType: 'tableau',
          };
        }
      } else {
        // eslint-disable-next-line no-console
        console.error('Not found tableau items:', topic);
      }
    }

    return panel;
  });

  return clonedTopic;
};

export const getPowerBIPageNameFromTopic = (topic, panel) => {
  const powerBiItem = get(
    topic,
    ['libraryItemGroups', 'powerBiLibraryItemList'],
    []
  )
    .map((item) =>
      get(item, 'powerBiLibraryItems', []).find(
        (pItem) => pItem.id === panel.libraryItemId
      )
    )
    .find(Boolean);
  return get(powerBiItem, ['data', 'pageName'], '');
};

export const getIsRTE = (panel) =>
  panel.type === Constants.LIBRARY_ITEM_TYPES.RICH_TEXT.value ||
  panel.type === Constants.TOPIC_PANEL_TYPES.RICH_TEXT.value;
